import dayjs from 'dayjs'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FarmerCreateOptionsType } from '~/api/services/farmers/index'
import { FormFields } from '~/components/FormFields'
import { useAuth } from '~/routes/components/AuthProvider'
import { capitalizeFirstLetter } from '~/utils/helpers'

interface AmountFarmerGroupProps {
  farmersOptions: FarmerCreateOptionsType
}

const AmountFarmerGroup: React.FC<AmountFarmerGroupProps> = (props) => {
  const { farmersOptions } = props

  const { t } = useTranslation()
  const { systemData: { amountUnit } } = useAuth()

  const { control } = useFormContext()
  const watchAmountType = useWatch({ control, name: 'amountType' })
  const wholeNumberErrorMessage = t('Please enter whole number')

  function renderBackwardsMonths(lastMonthIndex, arrayOfMonths) {
    const inputFields = []
    for (let i = lastMonthIndex, j = 0; i >= 0; i--, j++) {
      inputFields.push(<FormFields.Input
        control={control}
        typeInput={'controled'}
        allowDecimals={false}
        label={capitalizeFirstLetter(arrayOfMonths[i])}
        placeholder={t('Amount in {{amountUnit}}', { amountUnit })}
        rules={{
          required: {
            value: true,
            message: t('Please enter amount')
          },
          validate: v => (v - Math.floor(v) === 0) || wholeNumberErrorMessage
        }}
        name={`amounts[${i}].amount`}
        key={arrayOfMonths[i]}
      />)
    }
    return inputFields
  }

  function renderForwardMonths(lastMonthIndex, arrayOfMonths) {
    const inputFields = []
    for (let i = 11, j = lastMonthIndex + 1; i > lastMonthIndex; i--, j++) {
      inputFields.push(<FormFields.Input
        control={control}
        placeholder={t('Amount in {{amountUnit}}', { amountUnit })}
        label={capitalizeFirstLetter(arrayOfMonths[i])}
        typeInput={'controled'}
        allowDecimals={false}
        name={`amounts[${j}].amount`}
        rules={{
          required: {
            value: true,
            message: t('Please enter amount')
          },
          validate: v => (v - Math.floor(v) === 0) || wholeNumberErrorMessage
        }}
        key={arrayOfMonths[i]}
      />)
    }
    return inputFields
  }

  function renderMonths() {
    const lastMonthIndex = dayjs().month() - 1
    const arrayOfMonths = dayjs.months()
    return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
      {renderBackwardsMonths(lastMonthIndex, arrayOfMonths)}
      {renderForwardMonths(lastMonthIndex, arrayOfMonths)}
    </div>
  }

  return <>
    <FormFields.Select
      control={control}
      name={'amountType'}
      label={t('Amount type')}
      placeholder={t('Select amount type')}
      rules={{
        required: {
          value: true,
          message: t('Please select amount type')
        }
      }}
      selectOnBlur={false}
      options={farmersOptions.amountTypes.map(amountType => ({ value: amountType.key, text: amountType.label }))}
    />
    {watchAmountType === 'year' && <FormFields.Input
      control={control}
      label={t('Amount in {{amountUnit}}', { amountUnit })}
      typeInput={'controled'}
      allowDecimals={false}
      rules={{
        required: {
          value: watchAmountType === 'year',
          message: t('Please select amount type')
        },
        validate: v => (v - Math.floor(v) === 0) || wholeNumberErrorMessage
      }}
      placeholder={t('Amount in {{amountUnit}}', { amountUnit })}
      name='amount'
    />}
    {watchAmountType === 'month' && renderMonths()}
  </>
}
export default AmountFarmerGroup